import { useEffect, useState } from "react";
import { WhopCheckoutEmbed } from "@whop/checkout/react";
import {
  Sparkles,
  X,
  Check,
  Loader2,
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  Mail,
  Star,
  Receipt,
} from "lucide-react";
import {
  CREDIT_PACKS,
  startProCheckout,
  requestRestoreCode,
  verifyRestoreCode,
  refreshUsage,
  fetchPurchases,
} from "@/lib/subscription";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const CODE_RE = /^\d{6}$/;

export default function ProUpgradeModal({ open, onClose, reason }) {
  // View: "offer" | "checkout" | "restore" | "success"
  const [view, setView] = useState("offer");
  const [checkout, setCheckout] = useState(null); // { sessionId, environment, packId }
  const [status, setStatus] = useState("idle"); // idle | loading | unavailable | error
  const [errorMsg, setErrorMsg] = useState("");
  const [loadingPackId, setLoadingPackId] = useState(""); // which pack is opening

  // Restore state — two-step: enter email → enter code
  const [restoreStep, setRestoreStep] = useState("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [restoreStatus, setRestoreStatus] = useState("idle");
  const [restoreErr, setRestoreErr] = useState("");

  if (!open) return null;

  const limitHit = reason === "NO_CREDITS" || reason === "FREE_LIMIT_REACHED" || reason === "PLAN_LIMIT_REACHED";

  const handleClose = () => {
    setView("offer");
    setCheckout(null);
    setStatus("idle");
    setErrorMsg("");
    setEmail("");
    setCode("");
    setRestoreStep("email");
    setRestoreStatus("idle");
    setRestoreErr("");
    setLoadingPackId("");
    onClose?.();
  };

  const handleUpgrade = async (packId) => {
    setLoadingPackId(packId);
    setStatus("loading");
    setErrorMsg("");
    try {
      const res = await startProCheckout({ plan: packId });
      if (res?.ok && res.sessionId) {
        setCheckout({ sessionId: res.sessionId, environment: res.environment || "sandbox", packId });
        setView("checkout");
        setStatus("idle");
        return;
      }
      if (res?.ok && res.purchaseUrl) {
        // Session existed but only a hosted URL is available — open it.
        window.open(res.purchaseUrl, "_blank", "noopener,noreferrer");
        setStatus("idle");
        return;
      }
      if (res?.reason === "NOT_CONFIGURED" || res?.reason === "PLAN_NOT_CONFIGURED") {
        setStatus("unavailable");
        return;
      }
      setStatus("error");
      setErrorMsg("Could not open checkout. Please try again.");
    } catch {
      setStatus("error");
      setErrorMsg("Something went wrong. Please try again in a moment.");
    } finally {
      setLoadingPackId("");
    }
  };

  const handleRestoreRequest = async (e) => {
    e?.preventDefault?.();
    const value = email.trim();
    if (!EMAIL_RE.test(value)) {
      setRestoreErr("Please enter a valid email address.");
      return;
    }
    setRestoreErr("");
    setRestoreStatus("loading");
    try {
      const res = await requestRestoreCode(value);
      if (res?.reason === "TOO_MANY_REQUESTS") {
        setRestoreStatus("throttled");
        return;
      }
      setRestoreStep("code");
      setRestoreStatus("idle");
    } catch {
      setRestoreStatus("error");
      setRestoreErr("We couldn't send a code right now. Try again shortly.");
    }
  };

  const handleRestoreVerify = async (e) => {
    e?.preventDefault?.();
    const value = code.trim();
    if (!CODE_RE.test(value)) {
      setRestoreErr("Please enter the 6-digit code from your email.");
      return;
    }
    setRestoreErr("");
    setRestoreStatus("loading");
    try {
      const res = await verifyRestoreCode(email.trim(), value);
      if (res?.pro) {
        await refreshUsage();
        setView("success");
        setRestoreStatus("idle");
        return;
      }
      if (res?.reason === "TOO_MANY_REQUESTS") {
        setRestoreStatus("throttled");
        return;
      }
      setRestoreStatus("invalid");
    } catch {
      setRestoreStatus("error");
      setRestoreErr("Something went wrong. Try again in a moment.");
    }
  };

  const handleCheckoutComplete = async () => {
    // Whop iframe fired onComplete. The backend webhook is what actually
    // grants credits; poll usage a few times to catch the grant quickly.
    setView("success");
    for (let i = 0; i < 5; i++) {
      const u = await refreshUsage();
      if ((u?.creditsAvailable ?? u?.remaining ?? 0) > 0) break;
      await new Promise((r) => setTimeout(r, 1500));
    }
  };

  return (
    <div
      data-testid="pro-upgrade-modal"
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-[#0f172a]/55 backdrop-blur-md px-3 py-4 sm:px-4 sm:py-6"
      onClick={handleClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-lg max-h-[92vh] overflow-y-auto bg-white rounded-[1.75rem] border border-[#eae4d9] shadow-[0_24px_60px_-12px_rgba(15,23,42,0.35)] fade-up"
      >
        <button
          data-testid="pro-modal-close"
          onClick={handleClose}
          className="absolute top-3 right-3 z-20 w-9 h-9 rounded-full bg-white/95 hover:bg-white border border-[#eae4d9] shadow flex items-center justify-center transition"
          aria-label="Close"
        >
          <X className="w-4 h-4 text-[#0f172a]" />
        </button>

        {view === "offer" && (
          <OfferView
            limitHit={limitHit}
            status={status}
            errorMsg={errorMsg}
            loadingPackId={loadingPackId}
            onUpgrade={handleUpgrade}
            onRestore={() => setView("restore")}
            onClose={handleClose}
          />
        )}

        {view === "checkout" && checkout && (
          <CheckoutView
            checkout={checkout}
            onBack={() => setView("offer")}
            onComplete={handleCheckoutComplete}
          />
        )}

        {view === "restore" && (
          <RestoreView
            step={restoreStep}
            email={email}
            setEmail={setEmail}
            code={code}
            setCode={setCode}
            restoreStatus={restoreStatus}
            restoreErr={restoreErr}
            onRequest={handleRestoreRequest}
            onVerify={handleRestoreVerify}
            onEditEmail={() => {
              setRestoreStep("email");
              setRestoreStatus("idle");
              setRestoreErr("");
              setCode("");
            }}
            onBack={() => {
              setRestoreStep("email");
              setRestoreStatus("idle");
              setRestoreErr("");
              setView("offer");
            }}
          />
        )}

        {view === "success" && <SuccessView onClose={handleClose} />}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ views ---

function OfferView({ limitHit, status, errorMsg, loadingPackId, onUpgrade, onRestore, onClose }) {
  return (
    <>
      <div className="grain bg-[#0f172a] text-white px-6 pt-7 pb-7 relative overflow-hidden">
        <div className="pointer-events-none absolute -right-16 -top-16 w-56 h-56 rounded-full bg-[#ff6b4a]/25 blur-3xl" />
        <span
          data-testid="pro-modal-badge"
          className="relative inline-flex items-center gap-2 rounded-full bg-white/10 text-[#ff9d84] text-[10px] font-bold tracking-widest uppercase px-3 py-1.5"
        >
          <Sparkles className="w-3.5 h-3.5" /> Preview Packs
        </span>

        <h2
          data-testid="pro-modal-headline"
          className="relative mt-4 font-display text-2xl sm:text-3xl font-extrabold tracking-tight max-w-xs"
        >
          Ready to explore more grooming styles?
        </h2>
        <p
          data-testid="pro-modal-tagline"
          className="relative mt-3 text-sm sm:text-base text-white/90 max-w-sm leading-snug"
        >
          Unlock more previews and see which look suits your dog best.
        </p>

        {limitHit && (
          <p
            data-testid="pro-modal-limit-note"
            className="relative mt-3 text-xs text-[#ff9d84] font-semibold"
          >
            You've used your free previews. Pick a pack below to keep exploring.
          </p>
        )}
      </div>

      <div className="px-5 pt-5 pb-6">
        <div
          data-testid="pro-modal-packs"
          className="grid grid-cols-1 gap-3"
        >
          {CREDIT_PACKS.map((pack) => (
            <PackRow
              key={pack.id}
              pack={pack}
              loading={loadingPackId === pack.id}
              disabled={!!loadingPackId && loadingPackId !== pack.id}
              onSelect={() => onUpgrade(pack.id)}
            />
          ))}
        </div>

        {status === "unavailable" && (
          <p data-testid="pro-modal-unavailable" className="mt-3 text-center text-xs text-[#64748b]">
            Checkout is being finalised — this pack will be available shortly.
          </p>
        )}
        {status === "error" && errorMsg && (
          <p data-testid="pro-modal-error" className="mt-3 text-center text-xs text-[#c84125]">
            {errorMsg}
          </p>
        )}

        <p className="mt-4 text-center text-[11px] text-[#64748b]">
          Payments processed by Whop. Credits are added instantly after your payment is verified.
        </p>

        <button
          type="button"
          data-testid="pro-modal-restore-link"
          onClick={onRestore}
          className="mt-4 w-full inline-flex items-center justify-center gap-1.5 text-sm font-semibold text-[#0f172a] hover:text-[#c84125] py-2 transition-colors"
        >
          <Mail className="w-4 h-4" /> Already paid? Restore access
        </button>

        <button
          data-testid="pro-modal-dismiss"
          onClick={onClose}
          className="mt-1 w-full text-center text-sm font-medium text-[#64748b] hover:text-[#0f172a] py-2 transition-colors"
        >
          Not now
        </button>
      </div>
    </>
  );
}

function PackRow({ pack, loading, disabled, onSelect }) {
  const isPopular = pack.highlight === "Most Popular";
  const isBestValue = pack.highlight === "Best Value";
  return (
    <div
      data-testid={`pro-modal-pack-${pack.id}`}
      className={
        "relative rounded-2xl border p-4 sm:p-5 flex items-center gap-4 transition-all " +
        (isPopular
          ? "border-[#ff6b4a] bg-[#fff5f1] shadow-[0_8px_25px_-8px_rgba(255,107,74,0.35)]"
          : isBestValue
          ? "border-[#0f172a]/25 bg-white shadow-[0_6px_18px_-8px_rgba(15,23,42,0.25)]"
          : "border-[#eae4d9] bg-white")
      }
    >
      {isPopular && (
        <span
          data-testid={`pro-modal-pack-${pack.id}-badge`}
          className="absolute -top-2.5 left-4 inline-flex items-center gap-1 rounded-full bg-[#ff6b4a] text-white text-[9px] font-bold tracking-widest uppercase px-2 py-0.5"
        >
          <Star className="w-2.5 h-2.5" fill="currentColor" /> Most Popular
        </span>
      )}
      {isBestValue && (
        <span
          data-testid={`pro-modal-pack-${pack.id}-badge`}
          className="absolute -top-2.5 left-4 inline-flex items-center rounded-full bg-[#0f172a] text-white text-[9px] font-bold tracking-widest uppercase px-2 py-0.5"
        >
          Best Value
        </span>
      )}
      <div className="flex-1 min-w-0">
        <div className="text-xs font-bold tracking-widest uppercase text-[#c84125]">
          {pack.label}
        </div>
        <div className="mt-1 flex items-baseline gap-1.5">
          <span
            data-testid={`pro-modal-pack-${pack.id}-price`}
            className="font-display text-xl font-extrabold text-[#0f172a] leading-none"
          >
            {pack.price}
          </span>
          <span className="text-[11px] text-[#64748b]">{pack.currency}</span>
        </div>
        <p
          data-testid={`pro-modal-pack-${pack.id}-description`}
          className="mt-1 text-xs text-[#475569] leading-snug"
        >
          {pack.description}
        </p>
        {pack.savings && (
          <p
            data-testid={`pro-modal-pack-${pack.id}-savings`}
            className="mt-1 text-[10px] font-semibold text-[#c84125]"
          >
            {pack.savings}
          </p>
        )}
      </div>
      <button
        type="button"
        data-testid={`pro-modal-pack-${pack.id}-btn`}
        onClick={onSelect}
        disabled={loading || disabled}
        className={
          "shrink-0 inline-flex items-center justify-center gap-1.5 rounded-full font-bold tracking-wide uppercase px-4 py-2.5 text-xs transition-all duration-200 active:scale-[0.98] disabled:opacity-70 " +
          (isPopular
            ? "bg-[#ff6b4a] hover:bg-[#e85535] text-white shadow-[0_6px_18px_-4px_rgba(255,107,74,0.4)]"
            : "bg-[#0f172a] hover:bg-[#1e293b] text-white")
        }
      >
        {loading ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
        ) : (
          <>
            {pack.ctaLabel} <ArrowRight className="w-3.5 h-3.5" />
          </>
        )}
      </button>
    </div>
  );
}

function CheckoutView({ checkout, onBack, onComplete }) {
  const pack = CREDIT_PACKS.find((p) => p.id === checkout.packId) || CREDIT_PACKS[1];
  return (
    <div className="px-4 pt-4 pb-5 sm:px-5 sm:pt-5">
      <button
        type="button"
        data-testid="pro-modal-checkout-back"
        onClick={onBack}
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#64748b] hover:text-[#0f172a] transition-colors"
      >
        <ArrowLeft className="w-4 h-4" /> Back
      </button>
      <div className="mt-2">
        <div className="text-xs font-bold tracking-widest uppercase text-[#c84125]">
          {pack.label}
        </div>
        <div className="mt-0.5 flex items-baseline gap-2">
          <span className="font-display text-2xl font-extrabold text-[#0f172a]">{pack.price}</span>
          <span className="text-xs font-semibold text-[#64748b]">
            {pack.currency} · {pack.credits} AI previews
          </span>
        </div>
      </div>
      <div
        data-testid="pro-modal-whop-embed"
        className="mt-4 rounded-2xl border border-[#eae4d9] bg-[#fdfbf7] overflow-hidden"
      >
        <WhopCheckoutEmbed
          sessionId={checkout.sessionId}
          environment={checkout.environment}
          theme="light"
          themeOptions={{ accentColor: "#ff6b4a", borderRadius: 12 }}
          fallback={
            <div className="flex items-center justify-center gap-2 p-8 text-sm text-[#64748b]">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading secure checkout…
            </div>
          }
          onComplete={() => onComplete?.()}
        />
      </div>
      <p className="mt-3 text-center text-[11px] text-[#64748b]">
        Payments processed by Whop.
      </p>
    </div>
  );
}

function RestoreView({
  step,
  email,
  setEmail,
  code,
  setCode,
  restoreStatus,
  restoreErr,
  onRequest,
  onVerify,
  onEditEmail,
  onBack,
}) {
  const isEmailStep = step === "email";
  const [purchases, setPurchases] = useState([]);
  const [purchasesLoaded, setPurchasesLoaded] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      const res = await fetchPurchases();
      if (!alive) return;
      setPurchases(res.purchases || []);
      setPurchasesLoaded(true);
    })();
    return () => {
      alive = false;
    };
  }, []);

  return (
    <div className="px-6 pt-5 pb-6">
      <button
        type="button"
        data-testid="pro-modal-restore-back"
        onClick={onBack}
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#64748b] hover:text-[#0f172a] transition-colors"
      >
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <h3 className="font-display mt-3 text-2xl font-extrabold text-[#0f172a] tracking-tight">
        Restore your credits
      </h3>
      <p className="mt-1.5 text-sm text-[#64748b]">
        {isEmailStep
          ? "Enter the email you used at checkout. If it matches a verified purchase we'll email you a 6-digit code."
          : "Enter the 6-digit code we sent to your email. Credits are only restored after Whop confirms your purchase."}
      </p>

      {isEmailStep ? (
        <form onSubmit={onRequest} className="mt-5 space-y-3" data-testid="pro-modal-restore-form">
          <label className="block">
            <span className="text-xs font-bold tracking-widest uppercase text-[#64748b]">
              Purchase email
            </span>
            <input
              data-testid="pro-modal-restore-email"
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="mt-2 w-full rounded-full border border-[#eae4d9] bg-[#fdfbf7] px-4 py-3 text-sm text-[#0f172a] placeholder:text-[#94a3b8] outline-none focus:ring-4 focus:ring-[#ff6b4a]/25 focus:border-[#ff6b4a] transition"
            />
          </label>

          <button
            type="submit"
            data-testid="pro-modal-restore-submit"
            disabled={restoreStatus === "loading"}
            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#0f172a] hover:bg-[#111c33] disabled:opacity-70 text-white font-semibold px-6 py-3.5 transition"
          >
            {restoreStatus === "loading" ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Sending code…
              </>
            ) : (
              <>Send verification code</>
            )}
          </button>

          {restoreStatus === "throttled" && (
            <p data-testid="pro-modal-restore-throttled" className="text-center text-xs text-[#c84125]">
              Too many attempts. Please try again in a little while.
            </p>
          )}
          {restoreErr && (
            <p data-testid="pro-modal-restore-error" className="text-center text-xs text-[#c84125]">
              {restoreErr}
            </p>
          )}
          <p className="text-center text-[11px] text-[#64748b]">
            We never grant credits just from an email — you'll need the code from your inbox.
          </p>
        </form>
      ) : (
        <form onSubmit={onVerify} className="mt-5 space-y-3" data-testid="pro-modal-verify-form">
          <div className="text-xs text-[#64748b]">
            Code sent to <span className="font-semibold text-[#0f172a]">{email}</span>{" "}
            <button
              type="button"
              data-testid="pro-modal-restore-edit-email"
              onClick={onEditEmail}
              className="text-[#c84125] hover:text-[#a1301c] font-semibold underline underline-offset-2"
            >
              change
            </button>
          </div>

          <label className="block">
            <span className="text-xs font-bold tracking-widest uppercase text-[#64748b]">
              Verification code
            </span>
            <input
              data-testid="pro-modal-restore-code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              required
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="123456"
              className="mt-2 w-full rounded-full border border-[#eae4d9] bg-[#fdfbf7] px-4 py-3 text-center text-lg font-semibold tracking-[0.5em] text-[#0f172a] placeholder:text-[#cbd5e1] outline-none focus:ring-4 focus:ring-[#ff6b4a]/25 focus:border-[#ff6b4a] transition"
            />
          </label>

          <button
            type="submit"
            data-testid="pro-modal-verify-submit"
            disabled={restoreStatus === "loading"}
            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#0f172a] hover:bg-[#111c33] disabled:opacity-70 text-white font-semibold px-6 py-3.5 transition"
          >
            {restoreStatus === "loading" ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Verifying with Whop…
              </>
            ) : (
              <>Verify & restore credits</>
            )}
          </button>

          {restoreStatus === "invalid" && (
            <p data-testid="pro-modal-verify-invalid" className="text-center text-xs text-[#c84125]">
              That code is invalid or has expired. Request a new one if needed.
            </p>
          )}
          {restoreStatus === "throttled" && (
            <p data-testid="pro-modal-verify-throttled" className="text-center text-xs text-[#c84125]">
              Too many attempts. Please try again in a little while.
            </p>
          )}
          {restoreErr && (
            <p data-testid="pro-modal-verify-error" className="text-center text-xs text-[#c84125]">
              {restoreErr}
            </p>
          )}
          <p className="text-center text-[11px] text-[#64748b]">
            Credits are added only after Whop confirms your purchase.
          </p>
        </form>
      )}

      {/* Purchase history — this device's verified Whop purchases. */}
      <div className="mt-6 rounded-2xl border border-[#eae4d9] bg-[#fdfbf7] p-4">
        <div className="flex items-center gap-2">
          <Receipt className="w-4 h-4 text-[#c84125]" />
          <span className="text-xs font-bold tracking-widest uppercase text-[#0f172a]">
            Purchase history
          </span>
        </div>
        {!purchasesLoaded ? (
          <div className="mt-3 flex items-center gap-2 text-xs text-[#64748b]">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
          </div>
        ) : purchases.length === 0 ? (
          <p
            data-testid="pro-modal-purchase-history-empty"
            className="mt-2 text-xs text-[#64748b]"
          >
            No purchases on this device yet. When you buy a pack, it appears here with your receipt.
          </p>
        ) : (
          <ul
            data-testid="pro-modal-purchase-history"
            className="mt-3 space-y-2"
          >
            {purchases.slice(0, 10).map((p) => (
              <li
                key={p.eventId || p.whopPaymentId || p.createdAt}
                data-testid={`pro-modal-purchase-${p.entitlementKey}`}
                className="flex items-center justify-between gap-3 text-xs text-[#0f172a] bg-white border border-[#eae4d9] rounded-xl px-3 py-2"
              >
                <div className="min-w-0">
                  <div className="font-semibold truncate">
                    {readableEntitlement(p.entitlementKey)}
                  </div>
                  <div className="text-[10px] text-[#64748b]">
                    {p.creditsGranted} credits · {formatDate(p.createdAt)}
                  </div>
                </div>
                <span className="text-[10px] font-bold tracking-widest uppercase text-[#166534] bg-[#dcfce7] px-2 py-1 rounded-full">
                  {p.paymentStatus || "succeeded"}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function SuccessView({ onClose }) {
  return (
    <div className="px-6 pt-8 pb-8 text-center">
      <div className="mx-auto w-14 h-14 rounded-full bg-[#dcfce7] text-[#166534] flex items-center justify-center">
        <CheckCircle2 className="w-7 h-7" />
      </div>
      <h3
        data-testid="pro-modal-success"
        className="font-display mt-4 text-2xl font-extrabold text-[#0f172a] tracking-tight"
      >
        Your credits are ready
      </h3>
      <p className="mt-2 text-sm text-[#64748b]">
        Your preview credits have been added to this device.
      </p>
      <button
        type="button"
        data-testid="pro-modal-success-close"
        onClick={onClose}
        className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-[#ff6b4a] hover:bg-[#e85535] text-white font-semibold px-6 py-3 transition"
      >
        Start grooming <ArrowRight className="w-4 h-4" />
      </button>
    </div>
  );
}

function readableEntitlement(key) {
  if (key === "preview_pack_3") return "3 AI Preview Pack";
  if (key === "preview_pack_10") return "10 AI Preview Pack";
  if (key === "lifetime_access") return "100 AI Preview Pack";
  return key || "Preview credits";
}

function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return iso;
  }
}
